"use client";

import { useState, useEffect, useRef, CSSProperties } from "react";
import Image from "next/image";
import {
  Video,
  Armchair,
  Clock,
  CreditCard,
  Loader2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { loadStripe, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useCreatePaymentIntentMutation } from "@/redux/features/payment/paymentApi";
import { getGaClientId } from "@/lib/analytics";
import { ServiceTheme } from "./BookingStepper";
import { getBusinessTimezoneDisplay } from "@/lib/timezone";

// Brand colors matching BookingStepper theme colors
const themePrimaryColors: Record<ServiceTheme, { primary: string; hover: string }> = {
  counseling: { primary: "#896deb", hover: "#7a5de0" },
  lifeCoaching: { primary: "#DDA500", hover: "#c99400" },
  occupationalTherapy: { primary: "#C01610", hover: "#a01210" },
  psychology: { primary: "#C01610", hover: "#a01210" },
};

// Default blue theme (used when no service is selected yet)
const defaultThemePrimary = { primary: "#006DFF", hover: "#0059e6" };

type ThemeColor = { primary: string; hover: string };

const hexToRgba = (hex: string, alpha: number) => {
  const value = parseInt(hex.replace("#", ""), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Loaded once at module scope, per Stripe.js guidance
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || "");

// Card number/expiry/cvc are collected directly by Stripe Elements and never
// pass through our own state - only the non-sensitive billing fields do.
export interface PaymentData {
  cardholderName: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  suburb: string;
  city: string;
  postalCode: string;
  state: string;
}

interface PaymentAmounts {
  base_amount?: number;
  stripe_fee?: number;
  total?: number;
}

export interface PaymentProps {
  email?: string;
  onPaymentSubmit?: (data: PaymentData) => void;
  onPaymentSuccess?: (paymentIntentId: string) => void;
  onPaymentError?: (error: string) => void;
  bookingSummary?: {
    sessionType: "online" | "in-person";
    supportOption: string;
    duration: string;
    price: number;
    appointmentDate?: Date | null;
    appointmentTime?: string | null;
    practitionerName?: string | null;
    practitionerRole?: string | null;
    practitionerImage?: string | null;
  };
  timerMinutes?: number;
  // Stripe integration props
  appointmentId?: string;
  clientSecret?: string; // If provided externally
  serviceTheme?: ServiceTheme;
}

// Style passed to Stripe's hosted iframe inputs - Stripe only accepts text-level
// CSS here (no padding/box model), so borders/spacing stay on the wrapping div.
const stripeElementOptions = {
  style: {
    base: {
      fontSize: "14px",
      fontFamily: "var(--font-inter), sans-serif",
      color: "#262626",
      "::placeholder": { color: "rgba(0,0,0,0.5)" },
    },
    invalid: {
      color: "#dc2626",
    },
  },
};

// Payment Form Input
const PaymentInput = ({
  placeholder,
  value,
  onChange,
  onBlur,
  type = "text",
  inputMode,
  maxLength,
  className = "",
  themeColor,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: string;
  inputMode?: "text" | "numeric";
  maxLength?: number;
  className?: string;
  themeColor: ThemeColor;
}) => (
  <input
    type={type}
    inputMode={inputMode}
    maxLength={maxLength}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onBlur}
    placeholder={placeholder}
    className={`h-[34px] w-full bg-white px-[15px] py-[9px] font-inter text-sm font-normal text-neutral-800 placeholder:text-black/50 focus:outline-none focus:ring-1 ${className}`}
    style={{ "--tw-ring-color": hexToRgba(themeColor.primary, 0.3) } as CSSProperties}
  />
);

const sanitizeDigits = (value: string, maxLength: number) =>
  value.replace(/\D/g, "").slice(0, maxLength);

const isCardholderNameValid = (value: string) => value.trim().length > 1;

// Payment Summary Component
const PaymentSummary = ({
  sessionType,
  supportOption,
  duration,
  price,
  appointmentDate,
  appointmentTime,
  practitionerName,
  practitionerRole,
  practitionerImage,
  themeColor,
  serverAmounts,
}: NonNullable<PaymentProps["bookingSummary"]> & {
  themeColor: ThemeColor;
  serverAmounts?: PaymentAmounts | null;
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    const trimmed = time.trim();
    // Already 12-hour format (e.g. "5:45 PM") - just normalize it, don't reformat
    const twelveHourMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
    if (twelveHourMatch) {
      const [, hours, minutes, meridiem] = twelveHourMatch;
      return `${parseInt(hours, 10)}:${minutes} ${meridiem.toUpperCase()}`;
    }

    // 24-hour format (e.g. "17:45")
    const [hours, minutes] = trimmed.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const SessionIcon = sessionType === "online" ? Video : Armchair;
  // Stripe fee/total only ever come from the backend's create-intent response -
  // never guessed client-side, so what's shown always matches what gets charged.
  const numericPrice = serverAmounts?.base_amount ?? (Number(price) || 0);
  const stripeFee = serverAmounts?.stripe_fee;
  const total = serverAmounts?.total;

  return (
    <div className="flex flex-col">
      <h3 className="pb-1.5 font-onest text-xl font-semibold text-black">
        Booking Summary
      </h3>

      {/* Session Type Badge */}
      <div className="py-1.5">
        <div
          className="inline-flex items-center gap-2 rounded-[20px] px-4 py-2"
          style={{
            backgroundColor: themeColor.primary,
            boxShadow: "inset 0px 0px 20px 0px rgba(255,255,255,0.5)",
          }}
        >
          <SessionIcon className="size-5 text-white" />
          <span className="font-onest text-base font-medium text-white">
            {sessionType === "online" ? "Online Session" : "In-Person Session"}
          </span>
        </div>
      </div>

      {/* Practitioner Info */}
      {practitionerName && (
        <div className="py-1.5">
          <div className="flex items-center gap-2">
            <div className="relative size-[50px] overflow-hidden rounded-full">
              <Image
                src={practitionerImage || "/images/v3/booking/practionerSelection/practitioner-placeholder.jpg"}
                alt={practitionerName}
                fill
                className="object-cover"
                sizes="50px"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-onest text-lg font-semibold text-black">
                {practitionerName}
              </span>
              {practitionerRole && (
                <span className="font-onest text-sm font-normal text-black">
                  {practitionerRole}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Support Option */}
      <div className="py-1.5">
        <div className="border-l-2 pl-2" style={{ borderColor: themeColor.primary }}>
          <div className="py-1">
            <span className="font-onest text-sm font-semibold text-black">
              {supportOption}
            </span>
          </div>
          <div className="flex items-center gap-5 py-1">
            <div className="flex items-center gap-1">
              <Clock className="size-5" style={{ color: themeColor.primary }} />
              <span className="font-onest text-lg font-medium" style={{ color: themeColor.primary }}>
                {duration}
              </span>
            </div>
            <span className="font-onest text-lg font-bold text-black/60">
              ${price}
            </span>
          </div>
        </div>
      </div>

      {/* Appointment Details */}
      <div className="rounded-[10px] p-2" style={{ backgroundColor: hexToRgba(themeColor.primary, 0.1) }}>
        <div className="pb-1">
          <span className="font-onest text-sm font-semibold text-black">
            Appointment
          </span>
        </div>
        <div className="flex gap-2">
          <span className="font-onest text-sm font-normal text-black/60">
            {appointmentDate ? formatDate(appointmentDate) : "Select a date"}
          </span>
          <span className="font-onest text-sm font-normal text-black/60">
            {appointmentTime ? formatTime(appointmentTime) : "Select a time"}
          </span>
        </div>
        <div className="pt-0.5">
          <span className="font-onest text-xs font-normal text-black/30">
            {getBusinessTimezoneDisplay(appointmentDate)}
          </span>
        </div>
      </div>

      {/* Total Cost */}
      <div className="border-b border-black/20 py-1.5">
        <div className="pb-1">
          <span className="font-onest text-sm font-semibold text-black">
            Total Cost
          </span>
        </div>
        <div className="flex items-center justify-between py-1">
          <span className="font-onest text-base font-medium text-black/60">
            Session Fee
          </span>
          <span className="font-onest text-base font-normal text-black/60">
            ${numericPrice.toFixed(2)} AUD
          </span>
        </div>
        <div className="flex items-center justify-between py-1">
          <span className="font-onest text-base font-medium text-black/60">
            Stripe Fee
          </span>
          <span className="font-onest text-base font-normal text-black/60">
            {stripeFee !== undefined ? `$${stripeFee.toFixed(2)} AUD` : "Calculating..."}
          </span>
        </div>
      </div>

      {/* Total */}
      <div className="py-1.5">
        <div className="flex items-center justify-between">
          <span className="font-onest text-base font-medium text-black/60">
            Total
          </span>
          <span className="font-onest text-base font-normal text-black/60">
            {total !== undefined ? `$${total.toFixed(2)} AUD` : "Calculating..."}
          </span>
        </div>
      </div>
    </div>
  );
};

// Card brand icons
const CardBrands = () => (
  <div className="flex gap-[3px]">
    <div className="flex h-4 w-6 items-center justify-center rounded bg-red-500 text-[8px] font-bold text-white">
      MC
    </div>
    <div className="flex h-4 w-6 items-center justify-center rounded bg-blue-600 text-[8px] font-bold text-white">
      VISA
    </div>
    <div className="flex h-4 w-6 items-center justify-center rounded bg-blue-400 text-[8px] font-bold text-white">
      AMEX
    </div>
    <div className="flex h-4 w-6 items-center justify-center rounded bg-orange-500 text-[8px] font-bold text-white">
      DC
    </div>
  </div>
);

// Inner form - lives inside <Elements> so it can use useStripe()/useElements()
const PaymentForm = ({
  email = "",
  onPaymentSubmit,
  onPaymentSuccess,
  onPaymentError,
  bookingSummary,
  timerMinutes = 15,
  appointmentId,
  clientSecret: externalClientSecret,
  serviceTheme,
}: PaymentProps) => {
  const themeColor = serviceTheme ? themePrimaryColors[serviceTheme] : defaultThemePrimary;
  const stripe = useStripe();
  const elements = useElements();

  const [timeLeft, setTimeLeft] = useState(timerMinutes * 60);
  const [formData, setFormData] = useState<PaymentData>({
    cardholderName: "",
    country: "Australia",
    addressLine1: "",
    addressLine2: "",
    suburb: "",
    city: "",
    postalCode: "",
    state: "",
  });

  // Payment state
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(
    externalClientSecret || null,
  );
  const [paymentAmounts, setPaymentAmounts] = useState<PaymentAmounts | null>(null);
  const paymentIntentInitiated = useRef(false);

  // Card field validation state
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const [cardElementState, setCardElementState] = useState<{ complete: boolean; error?: string }>({
    complete: false,
  });

  const cardholderNameValid = isCardholderNameValid(formData.cardholderName);
  const isCardFormValid = cardElementState.complete && cardholderNameValid;

  const handleCardElementChange = (e: StripeCardElementChangeEvent) => {
    setCardElementState({ complete: e.complete, error: e.error?.message });
  };

  // Payment intent mutation
  const [createPaymentIntent, { isLoading: isCreatingIntent }] =
    useCreatePaymentIntentMutation();

  // Create payment intent when appointmentId is available
  useEffect(() => {
    const initPaymentIntent = async () => {
      if (!appointmentId || clientSecret || externalClientSecret || paymentIntentInitiated.current) {
        return;
      }

      paymentIntentInitiated.current = true;

      try {
        const result = await createPaymentIntent({
          appointment_id: appointmentId,
          ga_client_id: getGaClientId(),
          source: "booking_flow",
        }).unwrap();

        const data = result?.data;

        if (data?.client_secret) {
          setClientSecret(data.client_secret);
          setPaymentAmounts({
            base_amount: data.base_amount,
            stripe_fee: data.stripe_fee,
            total: data.total,
          });
        }
      } catch (error) {
        console.error("Failed to create payment intent:", error);
        setPaymentError("Failed to initialise payment. Please try again.");
        onPaymentError?.("Failed to create payment intent");
        paymentIntentInitiated.current = false; // allow retry
      }
    };

    initPaymentIntent();
  }, [appointmentId, clientSecret, externalClientSecret, createPaymentIntent, onPaymentError]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const updateField = <K extends keyof PaymentData>(
    field: K,
    value: PaymentData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!stripe || !elements || !clientSecret) {
      setPaymentError("Payment is not ready yet. Please wait a moment and try again.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setPaymentError("Card details not found. Please refresh and try again.");
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    try {
      onPaymentSubmit?.(formData);

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: formData.cardholderName,
            email: email || undefined,
            address: {
              line1: formData.addressLine1 || undefined,
              line2: formData.addressLine2 || undefined,
              city: formData.city || formData.suburb || undefined,
              state: formData.state || undefined,
              postal_code: formData.postalCode || undefined,
              country: "AU",
            },
          },
        },
      });

      if (error) {
        setPaymentStatus("error");
        setPaymentError(error.message || "Payment failed. Please check your card details and try again.");
        onPaymentError?.(error.message || "Payment failed");
      } else if (paymentIntent?.status === "succeeded") {
        setPaymentStatus("success");
        onPaymentSuccess?.(paymentIntent.id);
      } else {
        setPaymentStatus("error");
        setPaymentError("Payment could not be completed. Please try again.");
        onPaymentError?.("Payment not completed");
      }
    } catch (error) {
      console.error("Payment failed:", error);
      setPaymentStatus("error");
      setPaymentError(
        "Payment failed. Please check your card details and try again.",
      );
      onPaymentError?.("Payment failed");
    } finally {
      setIsProcessing(false);
    }
  };

  // Never guess this client-side - only show a dollar figure once the backend
  // has told us the real total, so the button always matches what gets charged.
  const payAmountLabel = paymentAmounts?.total !== undefined ? paymentAmounts.total.toFixed(2) : null;

  const cardSectionHasError =
    touched.stripeCard && (!cardElementState.complete || cardElementState.error);

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      {/* Header */}
      <div className="flex flex-col">
        <h2 className="p-2.5 font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
          Complete Payment
        </h2>
        <p className="max-w-[545px] p-2.5 font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
          Complete your payment within the next{" "}
          <span className="font-medium">{formatTimer(timeLeft)}</span> mins to
          confirm your appointment booking.
        </p>
      </div>

      {/* Main Content */}
      <div
        className="relative overflow-hidden rounded-[10px] border bg-white"
        style={{ borderColor: themeColor.primary }}
      >
        {/* Background Image - Positioned to show on the left, hidden on mobile */}
        <div className="absolute inset-y-0 left-0 hidden w-[calc(100%-441px)] lg:block">
          <Image
            src="/images/v3/booking/payment/942ab4773d870c2042c43cb90d2f47f23eb3e6eb.jpg"
            alt=""
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 0px, (max-width: 1526px) 100vw, 1526px"
          />
          {/* Gradient fade from image to white on the right */}
          <div
            className="absolute inset-y-0 right-0 w-[850px]"
            style={{
              background:
                "linear-gradient(to right, transparent 0%, rgba(255,255,255,0.3) 15%, rgba(255,255,255,0.6) 35%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,1) 75%)",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative flex flex-col items-start gap-4 lg:flex-row lg:items-start lg:justify-end lg:gap-[30px]">
          {/* Booking Summary - overlays the image on desktop, stacked on mobile */}
          {bookingSummary && (
            <div className="w-full rounded-[10px] p-4 md:p-[25px] lg:w-[420px] lg:shrink-0">
              <PaymentSummary {...bookingSummary} themeColor={themeColor} serverAmounts={paymentAmounts} />
            </div>
          )}

          {/* Payment Form - solid white background */}
          <div className="flex w-full flex-col gap-6 bg-white p-4 md:gap-9 md:p-[30px] lg:w-[441px] lg:shrink-0">
            {/* Contact Information */}
            <div className="flex flex-col gap-4">
              <p className="font-inter text-base font-medium text-neutral-500">
                Contact information
              </p>
              <div className="flex items-center rounded-[7px] border border-neutral-200 bg-neutral-100 p-3">
                <span className="font-inter text-sm font-medium text-neutral-600">
                  Email
                </span>
                <span className="flex-1 text-center font-inter text-sm font-normal text-neutral-800">
                  {email || "demo@email.com"}
                </span>
              </div>
            </div>

            {/* Payment Method */}
            <div className="flex flex-col gap-[30px]">
              <p className="font-inter text-base font-medium text-neutral-800">
                Payment method
              </p>

              <div className="flex flex-col gap-[25px]">
                {/* Card Information */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-inter text-sm font-medium text-neutral-500">
                      Card number <span className="text-red-500">*</span>
                    </p>
                    <div className="flex items-center">
                      <CardBrands />
                      <CreditCard className="ml-2 size-5 text-neutral-400" />
                    </div>
                  </div>
                  <div
                    className={`flex items-center rounded-[7px] border bg-white px-[15px] py-[9px] ${
                      touched.stripeCard && (!cardElementState.complete || cardElementState.error)
                        ? "border-red-400"
                        : "border-neutral-200"
                    }`}
                  >
                    <div className="flex-1">
                      <CardElement
                        options={{ ...stripeElementOptions, hidePostalCode: true }}
                        onChange={handleCardElementChange}
                        onBlur={() => markTouched("stripeCard")}
                      />
                    </div>
                  </div>
                  {cardSectionHasError && (
                    <span className="font-inter text-xs text-red-600">
                      {cardElementState.error || "Enter complete card details."}
                    </span>
                  )}
                </div>

                {/* Cardholder Name */}
                <div className="flex flex-col gap-1">
                  <p className="font-inter text-sm font-medium text-neutral-500">
                    Cardholder name <span className="text-red-500">*</span>
                  </p>
                  <div
                    className={`rounded-[7px] border bg-white ${
                      touched.cardholderName && !cardholderNameValid
                        ? "border-red-400"
                        : "border-neutral-200"
                    }`}
                  >
                    <PaymentInput
                      placeholder="Full name on card"
                      value={formData.cardholderName}
                      onChange={(value) => updateField("cardholderName", value)}
                      onBlur={() => markTouched("cardholderName")}
                      themeColor={themeColor}
                    />
                  </div>
                  {touched.cardholderName && !cardholderNameValid && (
                    <span className="font-inter text-xs text-red-600">
                      Enter the name on the card.
                    </span>
                  )}
                </div>

                {/* Country or Region */}
                <div className="flex flex-col gap-1">
                  <p className="font-inter text-sm font-medium text-neutral-500">
                    Country or region
                  </p>
                  <div className="flex flex-col">
                    {/* Country Select */}
                    <button
                      type="button"
                      className="flex h-[34px] items-center justify-between rounded-t-[7px] border border-black/10 bg-white px-[15px] py-[7px]"
                    >
                      <span className="font-inter text-sm font-normal text-neutral-800">
                        {formData.country}
                      </span>
                      {/* <ChevronDown className="size-[14px] text-neutral-400" /> */}
                    </button>

                    {/* Address Fields */}
                    <div className="border-x border-b border-neutral-200 bg-white">
                      <PaymentInput
                        placeholder="Address line 1"
                        value={formData.addressLine1}
                        onChange={(value) => updateField("addressLine1", value)}
                        themeColor={themeColor}
                      />
                    </div>
                    <div className="border-x border-b border-neutral-200 bg-white">
                      <PaymentInput
                        placeholder="Address line 2"
                        value={formData.addressLine2}
                        onChange={(value) => updateField("addressLine2", value)}
                        themeColor={themeColor}
                      />
                    </div>
                    <div className="border-x border-b border-neutral-200 bg-white">
                      <PaymentInput
                        placeholder="Suburb"
                        value={formData.suburb}
                        onChange={(value) => updateField("suburb", value)}
                        themeColor={themeColor}
                      />
                    </div>
                    <div className="flex">
                      <div className="flex-1 border-b border-l border-neutral-200 bg-white">
                        <PaymentInput
                          placeholder="City"
                          value={formData.city}
                          onChange={(value) => updateField("city", value)}
                          themeColor={themeColor}
                        />
                      </div>
                      <div className="flex-1 border-b border-r border-neutral-200 bg-white">
                        <PaymentInput
                          placeholder="Postal code"
                          value={formData.postalCode}
                          onChange={(value) =>
                            updateField("postalCode", sanitizeDigits(value, 4))
                          }
                          inputMode="numeric"
                          maxLength={4}
                          themeColor={themeColor}
                        />
                      </div>
                    </div>

                    {/* State Select */}
                    <button
                      type="button"
                      className="flex h-[34px] items-center justify-between rounded-b-[7px] border-x border-b border-black/10 bg-white px-[15px] py-[7px]"
                    >
                      <span className="font-inter text-sm font-normal text-black/50">
                        {formData.state || "State"}
                      </span>
                      {/* <ChevronDown className="size-[14px] text-neutral-400" /> */}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Status Messages */}
            {paymentError && (
              <div className="flex items-center gap-2 rounded-[7px] border border-red-200 bg-red-50 p-3">
                <AlertCircle className="size-5 text-red-500" />
                <span className="font-inter text-sm text-red-600">
                  {paymentError}
                </span>
              </div>
            )}

            {paymentStatus === "success" && (
              <div className="flex items-center gap-2 rounded-[7px] border border-green-200 bg-green-50 p-3">
                <CheckCircle className="size-5 text-green-500" />
                <span className="font-inter text-sm text-green-600">
                  Payment successful!
                </span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                isProcessing ||
                isCreatingIntent ||
                paymentStatus === "success" ||
                !isCardFormValid ||
                !stripe ||
                !elements ||
                !clientSecret
              }
              className="flex h-10 w-full items-center justify-center gap-2 rounded-[7px] font-inter text-base font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              style={
                {
                  backgroundColor: themeColor.primary,
                  "--tw-ring-color": themeColor.primary,
                } as CSSProperties
              }
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = themeColor.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = themeColor.primary;
              }}
            >
              {isCreatingIntent || !stripe ? (
                <>
                  <Loader2 className="size-5 animate-spin" />
                  Initialising...
                </>
              ) : isProcessing ? (
                <>
                  <Loader2 className="size-5 animate-spin" />
                  Processing...
                </>
              ) : paymentStatus === "success" ? (
                <>
                  <CheckCircle className="size-5" />
                  Payment Complete
                </>
              ) : (
                payAmountLabel ? `Pay $${payAmountLabel} AUD` : "Pay Now"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Outer component - provides the Stripe Elements context to the form
const Payment = (props: PaymentProps) => (
  <Elements stripe={stripePromise}>
    <PaymentForm {...props} />
  </Elements>
);

export default Payment;
